// ============================================
// Media Query Hooks - Type-Safe Responsive Design
// ============================================

import { useState, useEffect } from 'react';
import { BREAKPOINTS } from '../config/constants';
import type { DeviceInfo } from '../types/common';

/**
 * Custom hook for responsive design
 */
export const useMediaQuery = (query: string): boolean => {
    const [matches, setMatches] = useState(false);

    useEffect(() => {
        const media = window.matchMedia(query);
        if (media.matches !== matches) {
            setMatches(media.matches);
        }

        const listener = (e: MediaQueryListEvent) => setMatches(e.matches);
        media.addEventListener('change', listener);

        return () => media.removeEventListener('change', listener);
    }, [matches, query]);

    return matches;
};

/**
 * Hook to get current device type with type safety
 */
export const useDevice = (): DeviceInfo => {
    const isMobile = useMediaQuery(`(max-width: ${BREAKPOINTS.mobile}px)`);
    const isTablet = useMediaQuery(`(min-width: ${BREAKPOINTS.mobile + 1}px) and (max-width: ${BREAKPOINTS.tablet}px)`);
    const isLaptop = useMediaQuery(`(min-width: ${BREAKPOINTS.tablet + 1}px) and (max-width: ${BREAKPOINTS.desktop}px)`);
    const isDesktop = useMediaQuery(`(min-width: ${BREAKPOINTS.desktop + 1}px) and (max-width: ${BREAKPOINTS.tv}px)`);
    const isTV = useMediaQuery(`(min-width: ${BREAKPOINTS.tv + 1}px)`);
    const is4KTV = useMediaQuery(`(min-width: ${BREAKPOINTS.tv4k}px)`);
    const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');
    const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
    const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');

    return {
        isMobile,
        isTablet,
        isLaptop,
        isDesktop,
        isTV,
        is4KTV,
        isTouchDevice,
        prefersReducedMotion,
        prefersDarkMode,
        // Combined helpers
        isMobileOrTablet: isMobile || isTablet,
        isLargeScreen: isDesktop || isTV || is4KTV,
    };
};

/**
 * Hook to track window dimensions
 */
export interface WindowSize {
    width: number;
    height: number;
}

export const useWindowSize = (): WindowSize => {
    const [windowSize, setWindowSize] = useState<WindowSize>({
        width: typeof window !== 'undefined' ? window.innerWidth : 0,
        height: typeof window !== 'undefined' ? window.innerHeight : 0,
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight,
            });
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return windowSize;
};

export default useMediaQuery;
